import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function SubjectChips({ subjects }) {
  if (!subjects || subjects.length === 0) return <span className="text-xs text-teacher-text-tertiary">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {subjects.map(s => (
        <span key={s} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
          {SUBJECT_LABEL[s] || s}
        </span>
      ))}
    </div>
  );
}

export default function CoachingCenterDetail() {
  const { centerId } = useParams();
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    try {
      setLoading(true);
      const result = await api.getCoachingCenter(centerId);
      setCenter(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [centerId]);

  async function handleToggleActive() {
    const action = center.is_active ? 'deactivate' : 'reactivate';
    if (!confirm(`${action} this center?`)) return;
    try {
      if (center.is_active) {
        await api.deactivateCoachingCenter(centerId);
      } else {
        await api.updateCoachingCenter(centerId, { is_active: true });
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  function formatDate(d) {
    if (!d) return 'Never';
    try { return formatDistanceToNow(new Date(d), { addSuffix: true }); }
    catch { return 'Unknown'; }
  }

  if (loading) {
    return <div className="p-6"><div className="text-center py-12">Loading center...</div></div>;
  }
  if (error || !center) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Center not found'}
        </div>
      </div>
    );
  }

  const teachers = center.teachers || [];
  const batches = center.batches || [];
  const activeBatches = batches.filter(b => b.is_active !== false);
  const archivedBatches = batches.filter(b => b.is_active === false);

  return (
    <div className="p-6">
      <nav className="mb-6">
        <Link to="/coaching-centers" className="text-primary-600 hover:text-primary-800">
          ← Back to Coaching Centers
        </Link>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-teacher-text-primary mb-2">{center.name}</h1>
            <div className="space-y-1 text-sm text-teacher-text-secondary">
              {center.location && <div>📍 {center.location}</div>}
              {center.contact_email && <div>📧 {center.contact_email}</div>}
              {center.contact_phone && <div>📱 {center.contact_phone}</div>}
            </div>
          </div>
          <button
            onClick={handleToggleActive}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              center.is_active
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {center.is_active ? 'Deactivate' : 'Reactivate'}
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-teacher-border grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Status</div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              center.is_active ? 'bg-green-100 text-green-700' : 'bg-teacher-bg-secondary text-teacher-text-primary'
            }`}>{center.is_active ? 'Active' : 'Inactive'}</span>
          </div>
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Teachers</div>
            <div className="text-2xl font-bold text-primary-600">{center.total_teachers || 0}</div>
          </div>
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Batches</div>
            <div className="text-2xl font-bold text-primary-600">{center.total_batches || 0}</div>
          </div>
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Students</div>
            <div className="text-2xl font-bold text-primary-600">{center.total_students || 0}</div>
          </div>
        </div>
        <div className="mt-4 text-xs text-teacher-text-secondary">Created {formatDate(center.created_at)}</div>
      </div>

      {/* Teachers section */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border mb-6 overflow-hidden">
        <div className="p-6 border-b border-teacher-border flex justify-between items-center">
          <h2 className="text-lg font-semibold text-teacher-text-primary">Teachers ({teachers.length})</h2>
          <Link to="/teachers" className="text-sm text-primary-600 hover:text-primary-800">
            Add teacher in Teachers page →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Subjects</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Batches</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {teachers.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-teacher-text-secondary">No teachers in this center</td></tr>
              ) : (
                teachers.map(t => (
                  <tr key={t.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-3 text-sm font-medium text-teacher-text-primary">
                      {t.first_name} {t.last_name}
                    </td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{t.email}</td>
                    <td className="px-6 py-3"><SubjectChips subjects={t.subjects} /></td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{(t.batch_ids || []).length}</td>
                    <td className="px-6 py-3 text-sm">
                      <Link to={`/teachers/${t.id}`} className="text-primary-600 hover:text-primary-900">View →</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Batches section */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border overflow-hidden">
        <div className="p-6 border-b border-teacher-border flex justify-between items-center">
          <h2 className="text-lg font-semibold text-teacher-text-primary">
            Batches ({activeBatches.length} active{archivedBatches.length > 0 ? `, ${archivedBatches.length} archived` : ''})
          </h2>
          <Link
            to={`/batches?center_id=${centerId}`}
            className="text-sm text-primary-600 hover:text-primary-800"
          >
            Add batch in Batches page →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Classes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Teachers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {batches.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-teacher-text-secondary">No batches in this center</td></tr>
              ) : (
                batches.map(b => (
                  <tr key={b.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-3 text-sm font-medium text-teacher-text-primary">{b.name}</td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{(b.classes || []).join(', ') || '—'}</td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{(b.teacher_ids || []).length}</td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{b.total_students || 0}</td>
                    <td className="px-6 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        b.is_active !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-teacher-bg-secondary text-teacher-text-secondary'
                      }`}>{b.is_active !== false ? 'Active' : 'Archived'}</span>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <Link to={`/batches/${b.id}`} className="text-primary-600 hover:text-primary-900">View →</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
